import { useState, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { format, isPast } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Challenge type
 */
type ChallengeType = "quiz" | "photo" | "location" | "task" | "puzzle";

/**
 * Challenge data interface
 */
interface ChallengeData {
  id: string;
  title: string;
  description?: string;
  type: ChallengeType;
  isCompleted: boolean;
  completedByTeamId?: string;
  deadline?: string;
  rewardText?: string;
}

/**
 * Challenge type icons
 */
const CHALLENGE_ICONS: Record<ChallengeType, keyof typeof Ionicons.glyphMap> = {
  quiz: "help-circle",
  photo: "camera",
  location: "location",
  task: "checkbox",
  puzzle: "extension-puzzle",
};

/**
 * Challenge type labels
 */
const CHALLENGE_LABELS: Record<ChallengeType, string> = {
  quiz: "Quiz vraag",
  photo: "Foto opdracht",
  location: "Locatie check-in",
  task: "Opdracht",
  puzzle: "Puzzel",
};

/**
 * Props for RevealChallenges
 */
interface RevealChallengesProps {
  challenges: ChallengeData[];
  eventId: string;
  isAdmin?: boolean;
  onRefresh?: () => void;
}

/**
 * Single challenge card
 */
const ChallengeCard = ({
  challenge,
  onPress,
  colors,
}: {
  challenge: ChallengeData;
  onPress: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  const isExpired = challenge.deadline ? isPast(new Date(challenge.deadline)) : false;
  const statusColor = challenge.isCompleted
    ? colors.success
    : isExpired
    ? colors.error
    : colors.primary;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={challenge.isCompleted || isExpired}
      className="rounded-xl p-4 mb-3"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: challenge.isCompleted ? colors.success : colors.border,
        opacity: challenge.isCompleted || isExpired ? 0.7 : 1,
      }}
      accessibilityRole="button"
      accessibilityLabel={`${challenge.title} - ${challenge.isCompleted ? "Voltooid" : "Beschikbaar"}`}
    >
      <View className="flex-row items-center">
        {/* Icon */}
        <View
          className="w-12 h-12 rounded-full items-center justify-center"
          style={{ backgroundColor: challenge.isCompleted ? colors.successLight : colors.primaryLight }}
        >
          <Ionicons
            name={CHALLENGE_ICONS[challenge.type]}
            size={24}
            color={statusColor}
          />
        </View>

        {/* Content */}
        <View className="flex-1 ml-3">
          <Text className="font-semibold" style={{ color: colors.text }}>
            {challenge.title}
          </Text>
          <View className="flex-row items-center mt-1">
            <Text className="text-xs" style={{ color: colors.textSecondary }}>
              {CHALLENGE_LABELS[challenge.type]}
            </Text>
            {challenge.deadline && !challenge.isCompleted && (
              <Text className="text-xs ml-2" style={{ color: isExpired ? colors.error : colors.textTertiary }}>
                • {isExpired ? "Verlopen" : `Deadline: ${format(new Date(challenge.deadline), "d MMM HH:mm", { locale: nl })}`}
              </Text>
            )}
          </View>
          {challenge.description && (
            <Text className="text-sm mt-1" numberOfLines={2} style={{ color: colors.textSecondary }}>
              {challenge.description}
            </Text>
          )}
        </View>

        {/* Status */}
        <View className="ml-2">
          {challenge.isCompleted ? (
            <Ionicons name="checkmark-circle" size={24} color={colors.success} />
          ) : isExpired ? (
            <Ionicons name="close-circle" size={24} color={colors.error} />
          ) : (
            <Ionicons name="chevron-forward" size={24} color={colors.textTertiary} />
          )}
        </View>
      </View>

      {/* Reward */}
      {challenge.rewardText && (
        <View
          className="mt-3 px-3 py-2 rounded-lg flex-row items-center"
          style={{ backgroundColor: colors.warningLight }}
        >
          <Ionicons name="gift" size={16} color={colors.warning} />
          <Text className="ml-2 text-sm" style={{ color: colors.warning }}>
            Beloning: {challenge.rewardText}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

/**
 * Challenge submission modal
 */
const ChallengeModal = ({
  visible,
  challenge,
  onClose,
  onSubmit,
  isSubmitting,
  colors,
}: {
  visible: boolean;
  challenge: ChallengeData | null;
  onClose: () => void;
  onSubmit: (answer: string) => void;
  isSubmitting: boolean;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  const [answer, setAnswer] = useState("");

  if (!challenge) return null;

  const handleSubmit = () => {
    if (!answer.trim()) {
      Alert.alert("Fout", "Vul een antwoord in");
      return;
    }
    onSubmit(answer.trim());
    setAnswer("");
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <View
          className="rounded-t-3xl p-6"
          style={{ backgroundColor: colors.surface, maxHeight: "80%" }}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between mb-4">
            <Text className="text-xl font-bold flex-1 mr-4" style={{ color: colors.text }}>
              {challenge.title}
            </Text>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Challenge type badge */}
          <View className="flex-row items-center mb-4">
            <View
              className="px-3 py-1.5 rounded-full flex-row items-center"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons
                name={CHALLENGE_ICONS[challenge.type]}
                size={16}
                color={colors.primary}
              />
              <Text className="ml-2 text-sm font-medium" style={{ color: colors.primary }}>
                {CHALLENGE_LABELS[challenge.type]}
              </Text>
            </View>
          </View>

          {/* Description */}
          {challenge.description && (
            <Text className="mb-4" style={{ color: colors.textSecondary }}>
              {challenge.description}
            </Text>
          )}

          {/* Answer input */}
          <TextInput
            value={answer}
            onChangeText={setAnswer}
            placeholder={
              challenge.type === "quiz"
                ? "Typ je antwoord..."
                : "Beschrijf je bewijs of plak een link..."
            }
            placeholderTextColor={colors.textTertiary}
            multiline={challenge.type !== "quiz"}
            className="px-4 py-3 rounded-xl mb-4"
            style={{
              backgroundColor: colors.surfaceSecondary,
              color: colors.text,
              minHeight: challenge.type !== "quiz" ? 100 : undefined,
              textAlignVertical: challenge.type !== "quiz" ? "top" : "center",
            }}
          />

          {/* Submit button */}
          <TouchableOpacity
            onPress={handleSubmit}
            disabled={isSubmitting || !answer.trim()}
            className="py-4 rounded-xl items-center"
            style={{
              backgroundColor: isSubmitting || !answer.trim() ? colors.border : colors.primary,
            }}
          >
            {isSubmitting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="font-semibold text-white">Versturen</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

/**
 * RevealChallenges Component
 * Displays challenges for a reveal
 */
export const RevealChallenges = ({
  challenges,
  eventId,
  isAdmin = false,
  onRefresh,
}: RevealChallengesProps) => {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { profile, activeWeekend } = useAuth();

  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Count completed challenges
  const completedCount = challenges.filter((c) => c.isCompleted).length;

  /**
   * Handle challenge submission
   */
  const handleSubmit = useCallback(
    async (answer: string) => {
      if (!selectedChallenge || !profile?.id) return;

      setIsSubmitting(true);

      try {
        // Get user's team
        const { data: teamMember } = await supabase
          .from("team_members")
          .select("team_id")
          .eq("user_id", profile.id)
          .single();

        if (!teamMember?.team_id) {
          Alert.alert("Fout", "Je moet lid zijn van een team om deel te nemen");
          return;
        }

        // Submit the challenge
        const { error } = await supabase.from("challenge_submissions").insert({
          challenge_id: selectedChallenge.id,
          team_id: teamMember.team_id,
          submitted_by: profile.id,
          submission_type: selectedChallenge.type,
          submission_value: answer,
        });

        if (error) throw error;

        Alert.alert("Verzonden! 🎉", "Je antwoord is verstuurd. Een admin zal het beoordelen.", [
          {
            text: "OK",
            onPress: () => {
              setSelectedChallenge(null);
              onRefresh?.();
            },
          },
        ]);
      } catch (err: any) {
        console.error("Error submitting challenge:", err);
        Alert.alert("Fout", err.message || "Kon je antwoord niet versturen");
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedChallenge, profile?.id, onRefresh]
  );

  return (
    <View>
      {/* Progress */}
      <View className="flex-row items-center justify-between mb-3">
        <View className="flex-row items-center">
          <Ionicons name="trophy" size={20} color={colors.primary} />
          <Text className="ml-2 font-medium" style={{ color: colors.text }}>
            Voortgang
          </Text>
        </View>
        <View
          className="px-3 py-1 rounded-full"
          style={{ backgroundColor: colors.primaryLight }}
        >
          <Text className="text-sm font-medium" style={{ color: colors.primary }}>
            {completedCount} / {challenges.length}
          </Text>
        </View>
      </View>

      {/* Progress bar */}
      <View
        className="h-2 rounded-full mb-4 overflow-hidden"
        style={{ backgroundColor: colors.surfaceSecondary }}
      >
        <View
          className="h-full rounded-full"
          style={{
            backgroundColor: colors.primary,
            width: challenges.length > 0 ? `${(completedCount / challenges.length) * 100}%` : "0%",
          }}
        />
      </View>

      {/* Challenges list */}
      {challenges.map((challenge) => (
        <ChallengeCard
          key={challenge.id}
          challenge={challenge}
          onPress={() => setSelectedChallenge(challenge)}
          colors={colors}
        />
      ))}

      {/* All completed message */}
      {completedCount === challenges.length && challenges.length > 0 && (
        <View
          className="rounded-xl p-4 items-center mt-2"
          style={{ backgroundColor: colors.successLight }}
        >
          <Ionicons name="trophy" size={32} color={colors.success} />
          <Text className="mt-2 font-semibold" style={{ color: colors.success }}>
            Alle uitdagingen voltooid! 🎉
          </Text>
        </View>
      )}

      {/* Challenge modal */}
      <ChallengeModal
        visible={!!selectedChallenge}
        challenge={selectedChallenge}
        onClose={() => setSelectedChallenge(null)}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        colors={colors}
      />
    </View>
  );
};

export default RevealChallenges;
